import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { AccountInfo, AuthenticationResult, SilentRequest } from '@azure/msal-browser';
import { msalInstance, loginRequest, graphRequest, resetMsalInstance } from './authConfig';
import { buildApiUrl } from '../../config/apiConfig';
import { debugLog } from '../../utils/debugLogger';

// ============================================================================
// SESSION SECURITY CONFIGURATION - IDLE TIMEOUT
// ============================================================================
// Idle timeout in minutes - logout after this much inactivity
// Configurable via VITE_IDLE_TIMEOUT_MINUTES environment variable
const IDLE_TIMEOUT_MINUTES = parseInt(import.meta.env.VITE_IDLE_TIMEOUT_MINUTES || '120', 10);
// Warning shown X minutes before logout
const IDLE_WARNING_MINUTES = parseInt(import.meta.env.VITE_IDLE_WARNING_MINUTES || '5', 10);
// How often to check idle status (in milliseconds)
const IDLE_CHECK_INTERVAL_MS = 30 * 1000; // Check every 30 seconds
// Storage key for last activity timestamp
const LAST_ACTIVITY_KEY = 'auth_last_activity';
// Events that count as user activity
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

// Types
interface User {
  id: string;
  displayName: string;
  email: string;
  upn: string; // User Principal Name
  givenName: string;
  surname: string;
  jobTitle?: string;
  department?: string;
  photo?: string;
}

interface AdminStatus {
  isAdmin: boolean;
  isSuperAdmin: boolean;
  permissions: string[];
}

interface AuthConfig {
  auth_mode: 'azure';
  allow_public_chat: boolean;
  requires_admin_login: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  adminStatus: AdminStatus | null;
  isAdminLoading: boolean;
  authConfig: AuthConfig | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  resetAuth: () => Promise<void>;
  checkAdminStatus: () => Promise<void>;
  // Idle timeout security
  idleTimeoutMinutes: number;
  idleWarningMinutes: number;
  isIdleWarningShown: boolean;
  getIdleRemainingMinutes: () => number;
  resetIdleTimer: () => void;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider Props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth Provider Component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMsalInitialized, setIsMsalInitialized] = useState(false);
  const [adminStatus, setAdminStatus] = useState<AdminStatus | null>(null);
  const [isAdminLoading, setIsAdminLoading] = useState(true); // Start as true to prevent flickering
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  
  // Idle timeout state
  const [isIdleWarningShown, setIsIdleWarningShown] = useState(false);
  const idleCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const activityThrottleRef = useRef<number>(0);
  
  // Admin check deduplication - prevents multiple redundant API calls
  const adminCheckInProgressRef = useRef<boolean>(false);
  const adminCheckCompletedRef = useRef<boolean>(false);

  // ============================================================================
  // IDLE TIMEOUT SECURITY FUNCTIONS
  // ============================================================================
  
  /**
   * Record user activity - called on mouse/keyboard/scroll events
   * Throttled to avoid excessive writes
   */
  const recordActivity = useCallback(() => {
    const now = Date.now();
    // Throttle to once per 5 seconds to avoid performance issues
    if (now - activityThrottleRef.current < 5000) {
      return;
    }
    activityThrottleRef.current = now;
    sessionStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    
    // Clear warning if it was showing
    if (isIdleWarningShown) {
      setIsIdleWarningShown(false);
      console.log('🔐 [Idle Security] Activity detected - warning cleared');
    }
  }, [isIdleWarningShown]);

  /**
   * Reset idle timer explicitly (e.g., after user confirms they're still there)
   */
  const resetIdleTimer = useCallback(() => {
    sessionStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    setIsIdleWarningShown(false);
    console.log('🔐 [Idle Security] Idle timer reset by user action');
  }, []);

  /**
   * Get remaining minutes before idle logout
   */
  const getIdleRemainingMinutes = useCallback((): number => {
    const lastActivity = sessionStorage.getItem(LAST_ACTIVITY_KEY);
    if (!lastActivity) return IDLE_TIMEOUT_MINUTES;
    
    const elapsed = Date.now() - parseInt(lastActivity, 10);
    const timeoutMs = IDLE_TIMEOUT_MINUTES * 60 * 1000;
    const remaining = timeoutMs - elapsed;
    
    return Math.max(0, Math.floor(remaining / (60 * 1000)));
  }, []);

  /**
   * Check if user is idle (no activity for timeout period)
   */
  const isUserIdle = useCallback((): boolean => {
    const lastActivity = sessionStorage.getItem(LAST_ACTIVITY_KEY);
    if (!lastActivity) return false; // No activity recorded yet
    
    const elapsed = Date.now() - parseInt(lastActivity, 10);
    const timeoutMs = IDLE_TIMEOUT_MINUTES * 60 * 1000;
    
    return elapsed >= timeoutMs;
  }, []);

  /**
   * Check if warning should be shown (approaching idle timeout)
   */
  const shouldShowIdleWarning = useCallback((): boolean => {
    const lastActivity = sessionStorage.getItem(LAST_ACTIVITY_KEY);
    if (!lastActivity) return false;
    
    const elapsed = Date.now() - parseInt(lastActivity, 10);
    const timeoutMs = IDLE_TIMEOUT_MINUTES * 60 * 1000;
    const warningMs = (IDLE_TIMEOUT_MINUTES - IDLE_WARNING_MINUTES) * 60 * 1000;
    
    // Show warning when: (timeout - warning) <= elapsed < timeout
    return elapsed >= warningMs && elapsed < timeoutMs;
  }, []);

  /**
   * Clear idle tracking data (called on logout)
   */
  const clearIdleTracking = useCallback(() => {
    sessionStorage.removeItem(LAST_ACTIVITY_KEY);
    setIsIdleWarningShown(false);
  }, []);

  /**
   * Force logout due to idle timeout
   */
  const forceIdleLogout = useCallback(async (reason: string) => {
    console.warn(`🔐 [Idle Security] Forcing logout: ${reason}`);
    
    // Clear idle tracking
    clearIdleTracking();
    
    // Stop idle checks
    if (idleCheckIntervalRef.current) {
      clearInterval(idleCheckIntervalRef.current);
      idleCheckIntervalRef.current = null;
    }
    
    // Clear state first
    setUser(null);
    setIsAuthenticated(false);
    setAdminStatus(null);
    setIsLoading(true);
    setIsMsalInitialized(false);
    
    // Reset and reinitialize MSAL properly
    try {
      await resetMsalInstance();
      await msalInstance.initialize();
      setIsMsalInitialized(true);
    } catch (e) {
      console.error('Error resetting MSAL during force logout:', e);
      setIsMsalInitialized(true); // Allow login attempts anyway
    }
    
    setIsLoading(false);
    
    // Set flag to show friendly session expired message on login page
    sessionStorage.setItem('session_expired_reason', reason);
  }, [clearIdleTracking]);

  // Auth configuration - always Azure AD
  const loadAuthConfig = useCallback(async (): Promise<AuthConfig | null> => {
    const config: AuthConfig = { auth_mode: 'azure', allow_public_chat: false, requires_admin_login: true };
    setAuthConfig(config);
    return config;
  }, []);

  // Load user profile from Graph API
  const loadUserProfile = useCallback(async () => {
    try {
      const account = msalInstance.getActiveAccount();
      if (!account) return false;

      const request: SilentRequest = {
        scopes: graphRequest.scopes,
        account: account,
      };

      const response = await msalInstance.acquireTokenSilent(request);
      
      // Get user profile from Microsoft Graph
      const profileResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: {
          Authorization: `Bearer ${response.accessToken}`,
        },
      });

      if (profileResponse.ok) {
        const profile = await profileResponse.json();
        
        // Try to get user photo
        let photoUrl = '';
        try {
          const photoResponse = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
            headers: {
              Authorization: `Bearer ${response.accessToken}`,
            },
          });

          if (photoResponse.ok) {
            const photoBlob = await photoResponse.blob();
            photoUrl = URL.createObjectURL(photoBlob);
            debugLog('User photo loaded successfully');
          } else {
            debugLog('No user photo available or insufficient permissions');
          }
        } catch (photoError) {
          debugLog('Error fetching user photo:', photoError);
          // Continue without photo
        }

        const userData: User = {
          id: profile.id,
          displayName: profile.displayName || '',
          email: profile.mail || profile.userPrincipalName || '',
          upn: profile.userPrincipalName || '',
          givenName: profile.givenName || '',
          surname: profile.surname || '',
          jobTitle: profile.jobTitle || '',
          department: profile.department || '',
          photo: photoUrl,
        };

        setUser(userData);
        setIsAuthenticated(true);
        
        // Check admin status after authentication (deduplication handles preventing multiple calls)
        setTimeout(() => {
          checkAdminStatus();
        }, 500);
        
        return true;
      }
    } catch (error: any) {
      console.error('loadUserProfile error:', error?.name, error?.message);
      
      // If interaction is required, the cached session is stale
      if (error?.name === 'InteractionRequiredAuthError' || 
          error?.errorCode === 'interaction_required' ||
          error?.message?.includes('interaction_required') ||
          error?.message?.includes('AADSTS160021')) {
        console.warn('Session expired or requires interaction - need fresh login');
        // Clear the active account since it's stale
        msalInstance.setActiveAccount(null);
      }
      
      setUser(null);
      setIsAuthenticated(false);
      return false;
    }
    return false;
  }, []);

  // Get access token (Azure AD via MSAL)
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const account = msalInstance.getActiveAccount();
      if (!account) return null;

      const request: SilentRequest = {
        scopes: graphRequest.scopes,
        account: account,
      };

      const response = await msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      return null;
    }
  }, []);

  // Check admin status (Azure AD) - with deduplication
  const checkAdminStatus = useCallback(async (forceRefresh: boolean = false) => {
    // Prevent duplicate API calls - skip if already in progress or completed (unless force refresh)
    if (adminCheckInProgressRef.current) {
      debugLog('[Admin Check] Skipping - already in progress');
      return;
    }
    if (adminCheckCompletedRef.current && !forceRefresh) {
      debugLog('[Admin Check] Skipping - already completed this session');
      return;
    }
    
    adminCheckInProgressRef.current = true;
    try {
      setIsAdminLoading(true);
      const account = msalInstance.getActiveAccount();
      if (!account) {
        setAdminStatus({
          isAdmin: false,
          isSuperAdmin: false,
          permissions: []
        });
        adminCheckCompletedRef.current = true;
        return;
      }

      const request = {
        scopes: ['User.Read', 'Mail.Read', 'Files.Read.All', 'Calendars.Read', 'Chat.Read', 'ChannelMessage.Read.All', 'Team.ReadBasic.All', 'Channel.ReadBasic.All'],
        account: account,
      };

      const response = await msalInstance.acquireTokenSilent(request);
      const token = response.accessToken;

      // Call backend API to check admin status
      debugLog('[Admin Check] Making API call to /api/admin/auth/check');
      const adminResponse = await fetch(buildApiUrl('/api/admin/auth/check'), {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (adminResponse.ok) {
        const data = await adminResponse.json();
        if (data.success && data.authenticated) {
          setAdminStatus({
            isAdmin: data.admin_access || false,
            isSuperAdmin: data.super_admin_access || false,
            permissions: data.permissions || []
          });
        } else {
          setAdminStatus({
            isAdmin: false,
            isSuperAdmin: false,
            permissions: []
          });
        }
        adminCheckCompletedRef.current = true;
      } else {
        console.error('Failed to fetch admin status:', adminResponse.status, adminResponse.statusText);
        setAdminStatus({
          isAdmin: false,
          isSuperAdmin: false,
          permissions: []
        });
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setAdminStatus({
        isAdmin: false,
        isSuperAdmin: false,
        permissions: []
      });
    } finally {
      setIsAdminLoading(false);
      adminCheckInProgressRef.current = false;
    }
  }, []);

  // Check admin status when user becomes authenticated (handles page refresh)
  useEffect(() => {
    if (isAuthenticated && user && !isLoading) {
      debugLog('User authenticated, checking admin status...');
      // Small delay to ensure MSAL tokens are available
      const timer = setTimeout(() => {
        checkAdminStatus();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, user, isLoading, checkAdminStatus]);

  // Initialize authentication (Azure AD via MSAL)
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        debugLog('Starting auth initialization...');
        
        // Load auth configuration
        const config = await loadAuthConfig();
        if (!config) {
          setIsLoading(false);
          return;
        }

        debugLog('Auth config loaded:', config);

        // Initialize MSAL for Azure AD
        debugLog('Starting MSAL initialization...');
        debugLog('MSAL instance before init:', msalInstance);
        debugLog('MSAL config from instance:', msalInstance.getConfiguration());
        
        await msalInstance.initialize();
        debugLog('MSAL initialized successfully');
        setIsMsalInitialized(true);
        
        // Test that MSAL methods are working
        debugLog('Testing MSAL getAllAccounts method...');
        const testAccounts = msalInstance.getAllAccounts();
        debugLog('Test getAllAccounts result:', testAccounts);
        
        // Handle redirect response
        const tokenResponse = await msalInstance.handleRedirectPromise();
        debugLog('Redirect response handled:', tokenResponse);
        if (tokenResponse?.account) {
          msalInstance.setActiveAccount(tokenResponse.account);
          await loadUserProfile();
          setIsLoading(false);
          return;
        }

        // Check for existing accounts
        const accounts = msalInstance.getAllAccounts();
        debugLog('Existing accounts found:', accounts.length);
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
          
          // Try to load profile - if it fails, the session is stale
          const profileLoaded = await loadUserProfile();
          
          if (profileLoaded) {
            // Successfully loaded profile with existing session
            
            // Security check: Ensure session has valid activity tracking
            const lastActivity = sessionStorage.getItem(LAST_ACTIVITY_KEY);
            if (!lastActivity) {
              // Old session without activity tracking - record activity now
              console.log('🔐 [Idle Security] Existing session has no activity tracking - recording now');
              recordActivity();
            } else {
              // Check if user was idle too long before this page load
              const idleTimeMs = Date.now() - parseInt(lastActivity, 10);
              const maxIdleMs = IDLE_TIMEOUT_MINUTES * 60 * 1000;
              if (idleTimeMs > maxIdleMs) {
                console.warn(`🔐 [Idle Security] Session idle too long (${Math.floor(idleTimeMs / (60 * 1000))} min) - forcing re-login`);
                sessionStorage.removeItem(LAST_ACTIVITY_KEY);
                msalInstance.setActiveAccount(null);
                setIsAuthenticated(false);
                setUser(null);
                return;
              }
              
              console.log(`🔐 [Idle Security] Restored session, last active ${Math.floor(idleTimeMs / (60 * 1000))} min ago`);
              // Record activity for page reload
              recordActivity();
            }
            
            // Admin status check is handled by useEffect when auth state changes
            // No need for explicit check here - deduplication prevents duplicate calls
          } else {
            // Profile load failed - session is likely stale
            console.warn('Existing account session is stale, clearing active account');
            msalInstance.setActiveAccount(null);
            setIsAuthenticated(false);
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setIsMsalInitialized(true); // Allow to proceed even if init failed
        setIsAdminLoading(false); // No admin loading needed if auth failed
        // Silent fail - user will see login page
      } finally {
        debugLog('Setting isLoading to false, isAuthenticated:', isAuthenticated);
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // ============================================================================
  // ACTIVITY TRACKING FOR IDLE TIMEOUT
  // ============================================================================
  // Listens for user activity events and resets the idle timer
  useEffect(() => {
    if (!isAuthenticated || isLoading) {
      return;
    }

    // Handler for activity events
    const handleActivity = () => {
      recordActivity();
    };

    // Add event listeners for user activity
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Record initial activity when component mounts
    recordActivity();

    // Cleanup event listeners on unmount
    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated, isLoading, recordActivity]);

  // ============================================================================
  // IDLE TIMEOUT CHECK
  // ============================================================================
  // Periodically checks if user has been idle and shows warning/forces logout
  useEffect(() => {
    // Only run checks when authenticated
    if (!isAuthenticated || isLoading) {
      // Clear any existing interval if not authenticated
      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current);
        idleCheckIntervalRef.current = null;
      }
      return;
    }

    // Check idle status
    const runIdleCheck = async () => {
      // Check 1: Has user been idle too long?
      if (isUserIdle()) {
        await forceIdleLogout(`No activity for ${IDLE_TIMEOUT_MINUTES} minutes`);
        return;
      }
      
      // Check 2: Should we show warning?
      if (shouldShowIdleWarning() && !isIdleWarningShown) {
        setIsIdleWarningShown(true);
        const remaining = getIdleRemainingMinutes();
        console.log(`🔐 [Idle Security] Warning: Session will timeout in ${remaining} minutes`);
      }
    };

    // Run immediately
    runIdleCheck();

    // Set up periodic check
    idleCheckIntervalRef.current = setInterval(runIdleCheck, IDLE_CHECK_INTERVAL_MS);

    // Cleanup on unmount or when deps change
    return () => {
      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current);
        idleCheckIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, isLoading, isUserIdle, forceIdleLogout, shouldShowIdleWarning, isIdleWarningShown, getIdleRemainingMinutes]);

  // Login function (Azure AD via MSAL)
  const login = useCallback(async () => {
    try {
      debugLog('=== LOGIN FUNCTION CALLED ===');
      
      if (!isMsalInitialized) {
        console.warn('MSAL not initialized yet, waiting...');
        alert('Authentication system is still loading. Please wait a moment and try again.');
        return;
      }
      
      // Check if interaction is already in progress
      const existingAccounts = msalInstance.getAllAccounts();
      if (existingAccounts.length > 0) {
        debugLog('Existing cached accounts found:', existingAccounts.length);
        
        // Try to load profile with existing account
        try {
          const loaded = await loadUserProfile();
          if (loaded) {
            debugLog('Successfully loaded profile with existing session');
            return;
          }
        } catch (profileError: any) {
          console.warn('Failed to load profile with cached account:', profileError?.message);
        }
        
        // If profile load failed, the session is stale - clear and force fresh login
        debugLog('Session appears stale, clearing cached accounts and forcing fresh login...');
        existingAccounts.forEach(account => {
          msalInstance.setActiveAccount(null);
        });
      }
      
      debugLog('MSAL instance:', msalInstance);
      debugLog('Login request config:', loginRequest);
      
      // Check current accounts
      const accounts = msalInstance.getAllAccounts();
      debugLog('Current accounts before login:', accounts);
      
      debugLog('About to call msalInstance.loginPopup...');
      
      // Try popup login first
      try {
        const response: AuthenticationResult = await msalInstance.loginPopup(loginRequest);
        debugLog('=== LOGIN RESPONSE RECEIVED ===');
        debugLog('Login response:', response);
        
        if (response.account) {
          debugLog('Setting active account:', response.account);
          msalInstance.setActiveAccount(response.account);
          debugLog('Loading user profile...');
          await loadUserProfile();
          debugLog('User profile loaded successfully');
          // Record initial activity for idle timeout tracking
          recordActivity();
        } else {
          console.warn('No account in login response');
        }
      } catch (popupError: any) {
        console.warn('Popup login failed, trying redirect...', popupError);
        
        // Check if it's interaction in progress error
        if (popupError?.errorCode === 'interaction_in_progress') {
          console.log('Interaction already in progress, waiting...');
          alert('Login is already in progress. Please wait...');
          return;
        }
        
        // Show user-friendly error for popup blocked
        if (popupError?.name === 'BrowserAuthError' && 
            (popupError?.message?.includes('popup') || popupError?.message?.includes('blocked'))) {
          console.log('Popup was blocked by browser');
          alert('Login popup was blocked. Please allow popups for this site and try again.');
          return;
        }
        
        throw popupError;
      }
      
    } catch (error: any) {
      console.error('=== LOGIN ERROR ===');
      console.error('Login error details:', error);
      console.error('Error name:', error?.name);
      console.error('Error message:', error?.message);
      console.error('Error code:', error?.errorCode);
      console.error('Error stack:', error?.stack);
      
      // Check if it's a user cancellation
      if (error?.name === 'BrowserAuthError' && error?.message?.includes('user_cancelled')) {
        console.log('User cancelled the login');
      } else if (error?.errorCode === 'interaction_in_progress') {
        console.log('Interaction already in progress');
        alert('Login is already in progress. Please wait and avoid clicking multiple times.');
      } else {
        console.error('Unexpected login error, showing alert');
        alert('Login failed. Please try again or check your browser settings.');
      }
    }
  }, [loadUserProfile, isMsalInitialized]);

  // Logout function (Azure AD via MSAL)
  const logout = useCallback(async () => {
    try {
      console.log('🔐 [Session Security] User logging out');
      
      // Clear idle tracking
      clearIdleTracking();
      
      // Stop idle check interval
      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current);
        idleCheckIntervalRef.current = null;
      }
      
      // Reset admin check flags so a fresh check happens on re-login
      adminCheckCompletedRef.current = false;
      adminCheckInProgressRef.current = false;
      
      setUser(null);
      setIsAuthenticated(false);
      setAdminStatus(null);
      await msalInstance.logoutPopup();
    } catch (error) {
      // Silent fail but ensure state is cleared
      clearIdleTracking();
      adminCheckCompletedRef.current = false;
      adminCheckInProgressRef.current = false;
      setUser(null);
      setIsAuthenticated(false);
      setAdminStatus(null);
    }
  }, [clearIdleTracking]);

  // Reset authentication completely (Azure AD via MSAL)
  const resetAuth = useCallback(async () => {
    try {
      console.log('=== RESETTING AUTH COMPLETELY ===');
      
      // Clear idle tracking
      clearIdleTracking();
      
      // Stop idle check interval
      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current);
        idleCheckIntervalRef.current = null;
      }
      
      // Reset admin check flags so a fresh check happens on re-login
      adminCheckCompletedRef.current = false;
      adminCheckInProgressRef.current = false;
      
      setUser(null);
      setIsAuthenticated(false);
      setAdminStatus(null);
      setIsLoading(true);
      setIsMsalInitialized(false);
      
      // Reset MSAL instance completely
      await resetMsalInstance();
      
      // Re-initialize
      await msalInstance.initialize();
      setIsMsalInitialized(true);
      
      setIsLoading(false);
      console.log('Auth reset complete');
    } catch (error) {
      console.error('Error resetting auth:', error);
      adminCheckCompletedRef.current = false;
      adminCheckInProgressRef.current = false;
      setIsLoading(false);
      setIsMsalInitialized(true);
    }
  }, [clearIdleTracking]);

  // Context value
  const contextValue: AuthContextType = {
    user,
    isAuthenticated,
    isLoading: isLoading || !isMsalInitialized,
    adminStatus,
    isAdminLoading,
    authConfig,
    login,
    logout,
    getAccessToken,
    resetAuth,
    checkAdminStatus,
    // Idle timeout security
    idleTimeoutMinutes: IDLE_TIMEOUT_MINUTES,
    idleWarningMinutes: IDLE_WARNING_MINUTES,
    isIdleWarningShown,
    getIdleRemainingMinutes,
    resetIdleTimer,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Custom hook to check admin access
export const useAdminAccess = () => {
  const { adminStatus, isAdminLoading, checkAdminStatus } = useAuth();
  
  return {
    isAdmin: adminStatus?.isAdmin || false,
    isSuperAdmin: adminStatus?.isSuperAdmin || false,
    permissions: adminStatus?.permissions || [],
    isAdminLoading,
    checkAdminStatus,
    hasPermission: (permission: string) => adminStatus?.permissions.includes(permission) || false
  };
};

export default AuthProvider;